/**
 * Default factory + FieldConfig definitions for StickyContainerConfig
 *
 * Creates a bottom-anchored sticky container with one default Button.
 * Used by the editor when the user enables the sticky container toggle.
 *
 * FieldConfig constants define styling fields for the container settings panel,
 * reusing the standard StyleFieldRow component (same pattern as v2HeaderBarConfig).
 */

import type { StickyContainerConfig } from '@modules/screen-view-v2/types/stickyContainer';
import type { FieldConfig } from '../types/editorFieldTypes';
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';
import { createDefaultSection } from './flexibleSectionDefaults';

export function createDefaultStickyContainer(
  locale?: string,
  themeSettingsV2?: OnboardingThemeSettingsV2 | null,
): StickyContainerConfig {
  return {
    anchor: 'bottom',
    offset: { bottom: 0, start: 16, end: 16 },
    showBackground: true,
    backgroundColor: '#FFFFFF',
    opacity: 1.0,
    cornerRadius: { uniform: 12 },
    padding: { top: 8, bottom: 8, start: 16, end: 16 },
    sections: [createDefaultSection('PurchaseButton', 0, locale, themeSettingsV2)],
  };
}

// ─── FieldConfig definitions for Container Settings ──────────────────────────

const BASE = 'content.flexibleLayout.stickyContainer';

export const STICKY_BG_COLOR_CONFIG: FieldConfig = {
  path: `${BASE}.backgroundColor`,
  label: 'Background',
  type: 'color',
  defaultValue: '#FFFFFF',
  isNullable: false,
  guideKey: 'screenedit-sticky-container-background-color',
};

export const STICKY_OPACITY_CONFIG: FieldConfig = {
  path: `${BASE}.opacity`,
  label: 'Opacity',
  type: 'number',
  min: 0,
  max: 1,
  step: 0.05,
  defaultValue: 1.0,
  isNullable: false,
  guideKey: 'screenedit-sticky-container-opacity',
};

export const STICKY_CORNER_RADIUS_CONFIG: FieldConfig = {
  path: `${BASE}.cornerRadius.uniform`,
  label: 'Radius',
  type: 'number',
  min: 0,
  step: 1,
  defaultValue: 12,
  isNullable: false,
  guideKey: 'screenedit-sticky-container-corner-radius',
};

export const STICKY_BORDER_WIDTH_CONFIG: FieldConfig = {
  path: `${BASE}.border.width`,
  label: 'Border width',
  type: 'number',
  min: 0,
  step: 1,
  defaultValue: 0,
  isNullable: false,
  guideKey: 'screenedit-sticky-container-border-width',
};

export const STICKY_BORDER_COLOR_CONFIG: FieldConfig = {
  path: `${BASE}.border.color`,
  label: 'Border color',
  type: 'color',
  defaultValue: '#000000',
  isNullable: false,
  guideKey: 'screenedit-sticky-container-border-color',
};
